import { useEffect, useMemo, useState } from "react";
import {
  FiAlertCircle,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiChevronUp,
  FiClock,
  FiLoader,
  FiMap,
  FiMessageSquare,
  FiNavigation,
  FiPlus,
  FiSearch,
  FiSend,
  FiTrash2,
  FiVolume2,
  FiX,
  FiZap,
} from "react-icons/fi";
import { Sidebar } from "../components/Note";
import api from "../api";

const TAGS = ["Integraciones", "Mejoras", "Muy necesario", "Campañas", "Contactos", "Plantillas", "UI/UX", "Reportes"];

const STATUS_CFG = {
  nueva: {
    label: "Nueva",
    color: "#8b949e",
    bg: "rgba(139,148,158,0.12)",
    icon: <FiZap size={11} />,
  },
  en_consideracion: {
    label: "En consideración",
    color: "#d29922",
    bg: "rgba(210,153,34,0.12)",
    icon: <FiClock size={11} />,
  },
  en_desarrollo: {
    label: "En desarrollo",
    color: "#58a6ff",
    bg: "rgba(88,166,255,0.12)",
    icon: <FiNavigation size={11} />,
  },
  completada: {
    label: "Completada",
    color: "#25d366",
    bg: "rgba(37,211,102,0.12)",
    icon: <FiCheckCircle size={11} />,
  },
  rechazada: {
    label: "Rechazada",
    color: "#f85149",
    bg: "rgba(248,81,73,0.12)",
    icon: <FiX size={11} />,
  },
};

const ANNOUNCEMENTS = [
  {
    id: "a1",
    title: "Nuevo diseño de la plataforma",
    body: "Renovamos la interfaz de WaveSales para hacerla más moderna, rápida e intuitiva.",
    date: "15 Jun, 2025",
    type: "improvement",
  },
  {
    id: "a2",
    title: "Vista de campañas programadas",
    body: "Ahora puedes ver campañas en curso y programadas con más detalle desde Campañas.",
    date: "10 Jun, 2025",
    type: "feature",
  },
  {
    id: "a3",
    title: "Sistema de listas de contactos",
    body: "Puedes crear listas personalizadas para segmentar tus contactos y usarlas al enviar.",
    date: "05 Jun, 2025",
    type: "feature",
  },
  {
    id: "a4",
    title: "Corrección en sincronización de plantillas",
    body: "Resolvimos un problema de sincronización de plantillas con Meta.",
    date: "01 Jun, 2025",
    type: "fix",
  },
];

const ANNOUNCEMENT_TYPE = {
  feature: { label: "Nueva función", color: "#25d366", bg: "rgba(37,211,102,0.10)", icon: "🚀" },
  improvement: { label: "Mejora", color: "#58a6ff", bg: "rgba(88,166,255,0.10)", icon: "⚡" },
  fix: { label: "Corrección", color: "#d29922", bg: "rgba(210,153,34,0.10)", icon: "🔧" },
};

function getInitials(name) {
  const s = String(name || "").trim();
  if (!s) return "US";
  const parts = s.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function formatDate(raw) {
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

function uiStatusFromApi(status) {
  if (status === "development") return "en_desarrollo";
  if (status === "consideration") return "en_consideracion";
  return "nueva";
}

function apiStatusFromUi(status) {
  return status === "en_desarrollo" ? "development" : "consideration";
}

function normalizeIdea(it) {
  const when = new Date(it?.created_at || Date.now());
  return {
    id: String(it?.id || ""),
    apiId: it?.id,
    title: String(it?.title || ""),
    description: String(it?.description || ""),
    author: String(it?.created_by_username || "Usuario"),
    authorInitials: getInitials(it?.created_by_username || "Usuario"),
    date: formatDate(when),
    createdAtMs: when.getTime(),
    votes: Number(it?.votes_count || 0),
    hasVoted: !!it?.has_voted,
    status: uiStatusFromApi(it?.status),
    tags: [],
    commentsCount: Number(it?.comments_count || 0),
  };
}

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.en_consideracion;
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
}

function TagPill({ label }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs"
      style={{ background: "rgba(255,255,255,0.07)", color: "#8b949e" }}
    >
      #{label}
    </span>
  );
}

function IdeaCard({ idea, isAdmin, onVote, onClick, onStatusChange, onDelete }) {
  return (
    <div
      className="cursor-pointer rounded-xl border transition-all"
      style={{ background: "#161b22", borderColor: "#30363d" }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(37,211,102,0.35)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#30363d";
      }}
      onClick={() => onClick(idea)}
    >
      <div className="flex gap-4 p-5">
        <div className="flex shrink-0 flex-col items-center gap-1">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onVote(idea.id);
            }}
            className="flex min-w-[52px] flex-col items-center justify-center gap-0.5 rounded-xl border px-3 py-2 transition-all"
            style={{
              background: idea.hasVoted ? "rgba(37,211,102,0.12)" : "rgba(255,255,255,0.04)",
              borderColor: idea.hasVoted ? "rgba(37,211,102,0.4)" : "#30363d",
              color: idea.hasVoted ? "#25d366" : "#8b949e",
            }}
          >
            <FiChevronUp size={16} />
            <span className="text-sm font-bold leading-none">{idea.votes}</span>
          </button>
          <span className="text-[10px]" style={{ color: "#6e7681" }}>VOTAR</span>
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="mb-1.5 text-sm font-semibold leading-snug text-[#e6edf3]">{idea.title}</h3>
          <p className="mb-3 line-clamp-2 text-xs leading-relaxed text-[#8b949e]">{idea.description || "Sin descripción"}</p>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <div
                className="flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold"
                style={{ background: "rgba(37,211,102,0.15)", color: "#25d366" }}
              >
                {idea.authorInitials}
              </div>
              <span className="text-xs text-[#8b949e]">{idea.author}</span>
            </div>
            <span className="text-xs text-[#30363d]">•</span>
            <span className="inline-flex items-center gap-1 text-xs text-[#8b949e]">
              <FiCalendar size={11} />
              {idea.date}
            </span>
            {idea.tags.map((t) => (
              <TagPill key={`${idea.id}-${t}`} label={t} />
            ))}
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end justify-between gap-2">
          <StatusBadge status={idea.status} />
          <span className="inline-flex items-center gap-1 text-xs text-[#8b949e]">
            <FiMessageSquare size={13} />
            {Number(idea.commentsCount || 0)}
          </span>
          {isAdmin && (
            <div className="inline-flex items-center gap-1 rounded-lg border p-1" style={{ borderColor: "#30363d", background: "#21262d" }}>
              <button
                type="button"
                className="rounded-md px-2 py-1 text-[11px] font-medium"
                style={{
                  background: idea.status === "en_consideracion" ? "rgba(210,153,34,0.2)" : "transparent",
                  color: idea.status === "en_consideracion" ? "#d29922" : "#8b949e",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(idea.id, "en_consideracion");
                }}
              >
                Consideración
              </button>
              <button
                type="button"
                className="rounded-md px-2 py-1 text-[11px] font-medium"
                style={{
                  background: idea.status === "en_desarrollo" ? "rgba(88,166,255,0.2)" : "transparent",
                  color: idea.status === "en_desarrollo" ? "#58a6ff" : "#8b949e",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onStatusChange(idea.id, "en_desarrollo");
                }}
              >
                Desarrollo
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function IdeaModal({ idea, onClose, onVote, onCommentAdded, onDeleteIdea, onDeleteComment, isAdmin }) {
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [savingComment, setSavingComment] = useState(false);
  const [commentError, setCommentError] = useState("");

  useEffect(() => {
    let mounted = true;
    const loadComments = async () => {
      if (!idea?.apiId) {
        if (mounted) {
          setComments([]);
          setLoadingComments(false);
        }
        return;
      }
      try {
        if (mounted) {
          setLoadingComments(true);
          setCommentError("");
        }
        const { data } = await api.get(`roadmap/ideas/${idea.apiId}/comments/`);
        if (!mounted) return;
        setComments(Array.isArray(data?.results) ? data.results : []);
      } catch (e) {
        if (!mounted) return;
        const msg = e?.response?.data?.detail || "No se pudieron cargar los comentarios.";
        setCommentError(String(msg));
        setComments([]);
      } finally {
        if (mounted) setLoadingComments(false);
      }
    };

    loadComments();
    return () => {
      mounted = false;
    };
  }, [idea?.apiId]);

  const submitComment = async () => {
    const body = String(comment || "").trim();
    if (!body || !idea?.apiId || savingComment) return;
    try {
      setSavingComment(true);
      setCommentError("");
      const { data } = await api.post(`roadmap/ideas/${idea.apiId}/comments/`, { body });
      setComments((prev) => [...prev, data]);
      setComment("");
      if (typeof onCommentAdded === "function") {
        onCommentAdded(idea.id);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.body?.[0] || "No se pudo guardar el comentario.";
      setCommentError(String(msg));
    } finally {
      setSavingComment(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.75)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex max-h-[88vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border" style={{ background: "#161b22", borderColor: "#30363d" }}>
        <div className="flex items-start gap-4 border-b px-6 py-5" style={{ borderColor: "#30363d", background: "rgba(255,255,255,0.02)" }}>
          <button
            type="button"
            onClick={() => onVote(idea.id)}
            className="flex shrink-0 flex-col items-center justify-center gap-0.5 rounded-xl border px-3 py-2"
            style={{
              background: idea.hasVoted ? "rgba(37,211,102,0.12)" : "rgba(255,255,255,0.04)",
              borderColor: idea.hasVoted ? "rgba(37,211,102,0.4)" : "#30363d",
              color: idea.hasVoted ? "#25d366" : "#8b949e",
            }}
          >
            <FiChevronUp size={18} />
            <span className="text-base font-bold leading-none">{idea.votes}</span>
          </button>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-base font-semibold leading-snug text-[#e6edf3]">{idea.title}</h2>
              <div className="flex items-center gap-2">
                {isAdmin && (
                  <button
                    type="button"
                    title="Borrar idea"
                    onClick={() => onDeleteIdea(idea)}
                    className="rounded-lg p-1 transition-colors"
                    style={{ color: "rgba(248,81,73,0.75)" }}
                  >
                    <FiTrash2 size={15} />
                  </button>
                )}
                <button type="button" onClick={onClose} className="text-[#8b949e]">
                  <FiX size={18} />
                </button>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <StatusBadge status={idea.status} />
              {idea.tags.map((t) => (
                <TagPill key={`${idea.id}-md-${t}`} label={t} />
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#8b949e]">Descripción</p>
            <p className="text-sm leading-relaxed text-[#e6edf3]">{idea.description || "Sin descripción"}</p>
            <div className="mt-3 flex items-center gap-2">
              <div className="flex h-[22px] w-[22px] items-center justify-center rounded-full text-[9px] font-semibold" style={{ background: "rgba(37,211,102,0.15)", color: "#25d366" }}>
                {idea.authorInitials}
              </div>
              <span className="text-xs text-[#8b949e]">{idea.author} · {idea.date}</span>
            </div>
          </div>

          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-[#8b949e]">Comentarios ({comments.length})</p>
            {loadingComments ? (
              <p className="py-4 text-center text-xs text-[#8b949e]">Cargando comentarios...</p>
            ) : comments.length === 0 ? (
              <p className="py-4 text-center text-xs text-[#8b949e]">Sé el primero en comentar</p>
            ) : (
              <div className="space-y-3">
                {comments.map((c) => (
                  <div key={c.id} className="flex gap-3">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold" style={{ background: "rgba(37,211,102,0.12)", color: "#25d366" }}>
                      {c.author_initials || getInitials(c.author_username || c.author)}
                    </div>
                    <div className="flex-1 rounded-xl border p-3" style={{ background: "rgba(255,255,255,0.03)", borderColor: "#30363d" }}>
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-xs font-semibold text-[#e6edf3]">{c.author_username || c.author || "Usuario"}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-[#8b949e]">{formatDate(c.created_at || c.date)}</span>
                          {isAdmin && (
                            <button
                              type="button"
                              title="Borrar comentario"
                              onClick={() => onDeleteComment(c.id)}
                              className="rounded p-0.5 transition-colors"
                              style={{ color: "rgba(248,81,73,0.6)" }}
                            >
                              <FiTrash2 size={11} />
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-sm leading-relaxed text-[#e6edf3]">{c.body || c.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {commentError && (
              <p className="mt-3 rounded-lg border px-3 py-2 text-xs" style={{ borderColor: "rgba(248,81,73,0.35)", background: "rgba(248,81,73,0.1)", color: "#ffb4b0" }}>
                {commentError}
              </p>
            )}
          </div>
        </div>

        <div className="border-t px-6 py-4" style={{ borderColor: "#30363d", background: "rgba(255,255,255,0.01)" }}>
          <div className="flex gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold" style={{ background: "linear-gradient(135deg,#25d366,#128c7e)", color: "#0d1117" }}>
              US
            </div>
            <div className="flex flex-1 items-center gap-2 rounded-xl border px-3 py-2" style={{ background: "#21262d", borderColor: "#30363d" }}>
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitComment();
                }}
                placeholder="Escribe un comentario..."
                className="flex-1 bg-transparent text-sm text-[#e6edf3] outline-none"
              />
              <button
                type="button"
                onClick={submitComment}
                disabled={!String(comment || "").trim() || savingComment}
                className="disabled:opacity-50"
                style={{ color: String(comment || "").trim() ? "#25d366" : "#8b949e" }}
              >
                {savingComment ? <FiLoader className="animate-spin" size={15} /> : <FiSend size={15} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NewIdeaModal({ onClose, onCreate, isSaving }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);

  const toggleTag = (tag) => {
    setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((it) => it !== tag) : [...prev, tag]));
  };

  const canSubmit = String(title || "").trim().length >= 5 && String(description || "").trim().length >= 20 && !isSaving;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.75)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-2xl border" style={{ background: "#161b22", borderColor: "#30363d" }}>
        <div className="flex items-center justify-between border-b px-6 py-4" style={{ borderColor: "#30363d", background: "rgba(255,255,255,0.02)" }}>
          <div className="flex items-center gap-2">
            <FiZap size={16} style={{ color: "#d29922" }} />
            <h3 className="font-semibold text-[#e6edf3]">Nueva idea</h3>
          </div>
          <button type="button" onClick={onClose} className="text-[#8b949e]">
            <FiX size={16} />
          </button>
        </div>

        <div className="space-y-4 p-6">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#8b949e]">Título de la idea *</label>
            <input
              type="text"
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={100}
              placeholder="Describe tu idea en una oración clara..."
              className="w-full rounded-lg border px-3 py-2.5 text-sm text-[#e6edf3] outline-none"
              style={{ background: "#21262d", borderColor: title.length >= 5 ? "rgba(37,211,102,0.4)" : "#30363d" }}
            />
            <p className="mt-1 text-xs text-[#8b949e]">{title.length}/100 caracteres</p>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#8b949e]">Descripción detallada *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              maxLength={2000}
              placeholder="Explica el problema que resuelve, cómo debería funcionar y por qué es importante..."
              className="w-full resize-none rounded-lg border px-3 py-2.5 text-sm leading-relaxed text-[#e6edf3] outline-none"
              style={{ background: "#21262d", borderColor: description.length >= 20 ? "rgba(37,211,102,0.4)" : "#30363d" }}
            />
            {description.length > 0 && description.length < 20 && (
              <p className="mt-1 inline-flex items-center gap-1 text-xs" style={{ color: "#d29922" }}>
                <FiAlertCircle size={11} />
                Mínimo 20 caracteres
              </p>
            )}
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[#8b949e]">Etiquetas (opcional)</label>
            <div className="flex flex-wrap gap-2">
              {TAGS.map((tag) => {
                const active = selectedTags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className="rounded-full border px-2.5 py-1 text-xs font-medium transition-all"
                    style={{
                      background: active ? "rgba(37,211,102,0.12)" : "rgba(255,255,255,0.04)",
                      borderColor: active ? "rgba(37,211,102,0.4)" : "#30363d",
                      color: active ? "#25d366" : "#8b949e",
                    }}
                  >
                    {active && <FiCheck size={10} className="mr-1 inline" />}#{tag}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex gap-3 border-t px-6 py-4" style={{ borderColor: "#30363d" }}>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border px-4 py-2 text-sm font-medium"
            style={{ background: "transparent", borderColor: "#30363d", color: "#8b949e" }}
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => onCreate({ title: String(title || "").trim(), description: String(description || "").trim(), tags: selectedTags })}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all disabled:cursor-not-allowed"
            style={{ background: canSubmit ? "#25d366" : "#21262d", color: canSubmit ? "#0d1117" : "#8b949e" }}
          >
            {isSaving ? <FiLoader className="animate-spin" size={14} /> : <FiSend size={14} />} Publicar idea
          </button>
        </div>
      </div>
    </div>
  );
}

function RoadmapBoard({ ideas }) {
  const columns = [
    { status: "en_consideracion", title: "En consideración", icon: <FiClock size={15} /> },
    { status: "en_desarrollo", title: "En desarrollo", icon: <FiNavigation size={15} /> },
    { status: "completada", title: "Completadas", icon: <FiCheckCircle size={15} /> },
  ];

  return (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
      {columns.map((col) => {
        const colIdeas = ideas.filter((it) => it.status === col.status);
        const cfg = STATUS_CFG[col.status] || STATUS_CFG.en_consideracion;
        return (
          <div key={col.status} className="overflow-hidden rounded-xl border" style={{ background: "#161b22", borderColor: "#30363d" }}>
            <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: "#30363d", background: `${cfg.color}14` }}>
              <div className="flex items-center gap-2" style={{ color: cfg.color }}>
                {col.icon}
                <span className="text-sm font-semibold">{col.title}</span>
              </div>
              <span className="rounded-full px-2 py-0.5 text-xs font-bold" style={{ background: cfg.bg, color: cfg.color }}>
                {colIdeas.length}
              </span>
            </div>
            <div className="min-h-32 space-y-2.5 p-3">
              {colIdeas.length === 0 ? (
                <p className="py-8 text-center text-xs text-[#8b949e]">Sin ideas aquí aún</p>
              ) : (
                colIdeas.map((idea) => (
                  <div key={`col-${idea.id}`} className="rounded-lg border p-3" style={{ background: "rgba(255,255,255,0.02)", borderColor: "#30363d" }}>
                    <p className="mb-2 text-xs font-medium leading-snug text-[#e6edf3]">{idea.title}</p>
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center gap-1 text-xs text-[#25d366]">
                        <FiChevronUp size={12} />
                        <span className="font-bold">{idea.votes}</span>
                      </span>
                      <span className="inline-flex items-center gap-1 text-xs text-[#8b949e]">
                        <FiMessageSquare size={11} />
                        {Number(idea.commentsCount || 0)}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AnnouncementsView() {
  return (
    <div className="space-y-4">
      {ANNOUNCEMENTS.map((a) => {
        const type = ANNOUNCEMENT_TYPE[a.type] || ANNOUNCEMENT_TYPE.improvement;
        return (
          <div key={a.id} className="rounded-xl border p-6" style={{ background: "#161b22", borderColor: "#30363d" }}>
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl" style={{ background: type.bg, fontSize: 20 }}>
                {type.icon}
              </div>
              <div className="flex-1">
                <div className="mb-2 flex items-start justify-between gap-3">
                  <h3 className="text-[15px] font-semibold text-[#e6edf3]">{a.title}</h3>
                  <div className="flex shrink-0 items-center gap-2">
                    <span className="rounded-full px-2 py-0.5 text-xs font-medium" style={{ background: type.bg, color: type.color }}>{type.label}</span>
                    <span className="inline-flex items-center gap-1 text-xs text-[#8b949e]">
                      <FiCalendar size={11} />
                      {a.date}
                    </span>
                  </div>
                </div>
                <p className="text-sm leading-relaxed text-[#8b949e]">{a.body}</p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Roadmap() {
  const [tab, setTab] = useState("ideas");
  const [sortBy, setSortBy] = useState("votos");
  const [statusFilter, setStatusFilter] = useState("todas");
  const [search, setSearch] = useState("");

  const [ideas, setIdeas] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [detailIdea, setDetailIdea] = useState(null);
  const [showNewIdea, setShowNewIdea] = useState(false);
  const [savingIdea, setSavingIdea] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const [ideasRes, meRes] = await Promise.all([api.get("roadmap/ideas/"), api.get("me/")]);
      const rows = Array.isArray(ideasRes?.data?.results) ? ideasRes.data.results : [];
      setIdeas(rows.map(normalizeIdea));

      const me = meRes?.data || {};
      setIsAdmin(!!(me.is_staff || me.is_superuser));
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo cargar el roadmap.";
      setError(String(msg));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleVote = async (ideaId) => {
    const row = ideas.find((it) => it.id === ideaId);
    if (!row?.apiId) return;
    try {
      const { data } = await api.post(`roadmap/ideas/${row.apiId}/vote/`);
      const nextVotes = Number(data?.votes_count ?? row.votes ?? 0);
      const nextVoted = !!data?.voted;
      const nextCommentsCount = Number(data?.comments_count ?? row.commentsCount ?? 0);

      setIdeas((prev) =>
        prev.map((it) =>
          it.id === ideaId
            ? {
                ...it,
                votes: nextVotes,
                hasVoted: nextVoted,
                commentsCount: nextCommentsCount,
              }
            : it,
        ),
      );

      setDetailIdea((prev) =>
        prev && prev.id === ideaId
          ? {
              ...prev,
              votes: nextVotes,
              hasVoted: nextVoted,
              commentsCount: nextCommentsCount,
            }
          : prev,
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo registrar el voto.";
      setError(String(msg));
    }
  };

  const handleCreateIdea = async ({ title, description, tags }) => {
    try {
      setSavingIdea(true);
      setError("");
      const { data } = await api.post("roadmap/ideas/", { title, description });
      const created = { ...normalizeIdea(data), tags: Array.isArray(tags) ? tags : [] };
      setIdeas((prev) => [created, ...prev]);
      setShowNewIdea(false);
      setTab("ideas");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.response?.data?.title?.[0] || "No se pudo crear la idea.";
      setError(String(msg));
    } finally {
      setSavingIdea(false);
    }
  };

  const handleDeleteIdea = async (idea) => {
    if (!isAdmin) return;
    if (!window.confirm(`¿Borrar la idea "${idea.title}"? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`roadmap/ideas/${idea.apiId}/`);
      setIdeas((prev) => prev.filter((it) => it.id !== idea.id));
      setDetailIdea(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo borrar la idea.";
      setError(String(msg));
    }
  };

  const handleDeleteComment = async (ideaId, commentId) => {
    if (!isAdmin) return;
    const row = ideas.find((it) => it.id === ideaId);
    if (!row?.apiId) return;
    try {
      await api.delete(`roadmap/ideas/${row.apiId}/comments/${commentId}/`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo borrar el comentario.";
      setError(String(msg));
    }
  };

  const handleStatusChange = async (ideaId, nextUiStatus) => {
    if (!isAdmin) return;
    try {
      const row = ideas.find((it) => it.id === ideaId);
      if (!row?.apiId) return;
      const payload = { status: apiStatusFromUi(nextUiStatus) };
      const { data } = await api.patch(`roadmap/ideas/${row.apiId}/status/`, payload);
      const normalized = normalizeIdea(data);
      setIdeas((prev) =>
        prev.map((it) =>
          it.id === ideaId
            ? {
                ...it,
                ...normalized,
                votes: it.votes,
                hasVoted: it.hasVoted,
                tags: it.tags,
                commentsCount: it.commentsCount,
              }
            : it,
        ),
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo actualizar el estado.";
      setError(String(msg));
    }
  };

  const filteredIdeas = useMemo(() => {
    const text = String(search || "").trim().toLowerCase();
    const base = ideas.filter((it) => {
      const matchText = !text || it.title.toLowerCase().includes(text) || it.description.toLowerCase().includes(text);
      const matchStatus = statusFilter === "todas" || it.status === statusFilter;
      return matchText && matchStatus;
    });

    const sorted = [...base];
    sorted.sort((a, b) => {
      if (sortBy === "recientes") return b.createdAtMs - a.createdAtMs;
      if (sortBy === "comentarios") return Number(b.commentsCount || 0) - Number(a.commentsCount || 0);
      return b.votes - a.votes;
    });
    return sorted;
  }, [ideas, search, statusFilter, sortBy]);

  const stats = useMemo(() => {
    return {
      totalIdeas: ideas.length,
      inDevelopment: ideas.filter((it) => it.status === "en_desarrollo").length,
      completed: ideas.filter((it) => it.status === "completada").length,
      totalVotes: ideas.reduce((acc, it) => acc + Number(it.votes || 0), 0),
    };
  }, [ideas]);

  return (
    <div className="min-h-screen" style={{ background: "#0d1117", color: "#e6edf3" }}>
      <div className="flex min-h-screen">
        <Sidebar />

        <main className="flex-1 overflow-y-auto px-4 py-6 md:px-6 lg:px-8">
          <section className="mx-auto w-full max-w-[1200px] space-y-5">
            <header className="rounded-2xl border px-5 py-5" style={{ background: "#161b22", borderColor: "#30363d" }}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h1 className="text-[28px] font-semibold leading-tight text-[#f0f6fc]">Roadmap & Ideas</h1>
                  <p className="mt-1 text-sm text-[#8b949e]">Propón mejoras, vota y sigue el progreso</p>
                </div>
                <span
                  className="inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold"
                  style={{ background: "rgba(255,255,255,0.03)", borderColor: "#30363d", color: "#8b949e" }}
                >
                  {isAdmin ? "Modo administrador" : "Modo colaborador"}
                </span>
              </div>
            </header>

            <section className="space-y-4 rounded-2xl border p-5" style={{ background: "#0f1723", borderColor: "#30363d" }}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="mb-1 flex items-center gap-2">
                    <FiMap size={18} style={{ color: "#25d366" }} />
                    <h2 className="text-2xl font-semibold text-[#f0f6fc]">Roadmap público de WaveSales</h2>
                  </div>
                  <p className="text-sm text-[#8b949e]">Comparte ideas, vota por las que más te interesan y sigue el progreso de las mejoras.</p>
                </div>
                {tab === "ideas" && (
                  <button
                    type="button"
                    onClick={() => setShowNewIdea(true)}
                    className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold"
                    style={{ background: "#25d366", color: "#0d1117" }}
                  >
                    <FiPlus size={15} />
                    Agregar idea
                  </button>
                )}
              </div>

              <div className="w-fit rounded-lg p-1" style={{ background: "#21262d" }}>
                {[
                  { id: "ideas", label: "Ideas", icon: <FiZap size={14} />, count: ideas.length },
                  { id: "roadmap", label: "Roadmap", icon: <FiMap size={14} /> },
                  { id: "anuncios", label: "Anuncios", icon: <FiVolume2 size={14} />, count: ANNOUNCEMENTS.length },
                ].map((t) => {
                  const active = tab === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTab(t.id)}
                      className="inline-flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-medium transition-all"
                      style={{
                        background: active ? "#161b22" : "transparent",
                        color: active ? "#e6edf3" : "#8b949e",
                        boxShadow: active ? "0 1px 3px rgba(0,0,0,0.3)" : "none",
                      }}
                    >
                      {t.icon}
                      {t.label}
                      {typeof t.count === "number" && (
                        <span
                          className="rounded-full px-1.5 py-0.5 text-xs font-bold"
                          style={{ background: active ? "rgba(37,211,102,0.15)" : "rgba(255,255,255,0.08)", color: active ? "#25d366" : "#8b949e" }}
                        >
                          {t.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {error && (
                <div className="rounded-lg border px-3 py-2 text-sm" style={{ borderColor: "rgba(248,81,73,0.35)", background: "rgba(248,81,73,0.1)", color: "#ffb4b0" }}>
                  {error}
                </div>
              )}

              {tab === "ideas" && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                    {[
                      { label: "Total ideas", value: stats.totalIdeas, color: "#e6edf3" },
                      { label: "En desarrollo", value: stats.inDevelopment, color: "#58a6ff" },
                      { label: "Completadas", value: stats.completed, color: "#25d366" },
                      { label: "Votos totales", value: stats.totalVotes, color: "#d29922" },
                    ].map((s) => (
                      <div key={s.label} className="rounded-xl border p-4" style={{ background: "#161b22", borderColor: "#30363d" }}>
                        <p className="text-[30px] font-bold leading-tight" style={{ color: s.color }}>{s.value}</p>
                        <p className="mt-1 text-xs text-[#8b949e]">{s.label}</p>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-lg border px-3 py-2" style={{ background: "#21262d", borderColor: "#30363d" }}>
                      <FiSearch size={14} style={{ color: "#8b949e" }} />
                      <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar ideas..."
                        className="flex-1 bg-transparent text-sm text-[#e6edf3] outline-none"
                      />
                      {search && (
                        <button type="button" onClick={() => setSearch("")} className="text-[#8b949e]">
                          <FiX size={13} />
                        </button>
                      )}
                    </div>

                    <div className="rounded-lg p-1" style={{ background: "#21262d" }}>
                      {[
                        { value: "votos", label: "Más votadas" },
                        { value: "recientes", label: "Recientes" },
                        { value: "comentarios", label: "Más comentadas" },
                      ].map((opt) => {
                        const active = sortBy === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setSortBy(opt.value)}
                            className="rounded-md px-3 py-1 text-xs font-medium"
                            style={{
                              background: active ? "#161b22" : "transparent",
                              color: active ? "#e6edf3" : "#8b949e",
                              boxShadow: active ? "0 1px 3px rgba(0,0,0,0.3)" : "none",
                            }}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: "todas", label: "Todas" },
                      { value: "nueva", label: "Nuevas" },
                      { value: "en_consideracion", label: "En consideración" },
                      { value: "en_desarrollo", label: "En desarrollo" },
                      { value: "completada", label: "Completadas" },
                    ].map((f) => {
                      const active = statusFilter === f.value;
                      const cfg = STATUS_CFG[f.value];
                      return (
                        <button
                          key={f.value}
                          type="button"
                          onClick={() => setStatusFilter(f.value)}
                          className="inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium"
                          style={{
                            background: active ? cfg?.bg || "rgba(37,211,102,0.1)" : "transparent",
                            borderColor: active ? cfg?.color || "rgba(37,211,102,0.4)" : "#30363d",
                            color: active ? cfg?.color || "#25d366" : "#8b949e",
                          }}
                        >
                          {cfg?.icon}
                          {f.label}
                        </button>
                      );
                    })}
                  </div>

                  {loading ? (
                    <div className="rounded-xl border p-10 text-center text-[#8b949e]" style={{ background: "#161b22", borderColor: "#30363d" }}>
                      Cargando ideas...
                    </div>
                  ) : filteredIdeas.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-3 py-16">
                      <FiZap size={32} style={{ color: "#8b949e" }} />
                      <p className="text-sm text-[#8b949e]">No se encontraron ideas</p>
                      <button
                        type="button"
                        onClick={() => setShowNewIdea(true)}
                        className="inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium"
                        style={{ background: "#25d366", color: "#0d1117" }}
                      >
                        <FiPlus size={14} />
                        Sé el primero en proponer una
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredIdeas.map((idea) => (
                        <IdeaCard
                          key={idea.id}
                          idea={idea}
                          isAdmin={isAdmin}
                          onVote={handleVote}
                          onClick={setDetailIdea}
                          onStatusChange={handleStatusChange}
                          onDelete={handleDeleteIdea}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {tab === "roadmap" && <RoadmapBoard ideas={ideas} />}
              {tab === "anuncios" && <AnnouncementsView />}
            </section>
          </section>
        </main>
      </div>

      {detailIdea && (
        <IdeaModal
          idea={detailIdea}
          onClose={() => setDetailIdea(null)}
          onVote={handleVote}
          isAdmin={isAdmin}
          onDeleteIdea={handleDeleteIdea}
          onDeleteComment={(commentId) => handleDeleteComment(detailIdea.id, commentId)}
          onCommentAdded={(ideaId) => {
            setIdeas((prev) =>
              prev.map((it) =>
                it.id === ideaId
                  ? {
                      ...it,
                      commentsCount: Number(it.commentsCount || 0) + 1,
                    }
                  : it,
              ),
            );
            setDetailIdea((prev) =>
              prev && prev.id === ideaId
                ? {
                    ...prev,
                    commentsCount: Number(prev.commentsCount || 0) + 1,
                  }
                : prev,
            );
          }}
        />
      )}
      {showNewIdea && <NewIdeaModal onClose={() => setShowNewIdea(false)} onCreate={handleCreateIdea} isSaving={savingIdea} />}
    </div>
  );
}

export default Roadmap;
